import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Grid, IconButton, makeStyles, Typography, Tooltip, Checkbox } from "@material-ui/core";
import styles from "../styles";
import { Autocomplete, CustomTextField, DateTime } from "@totalsoft/rocket-webapp-components-deprecated";
import { get, set } from "@totalsoft/change-tracking-react";
import { emptyString } from "constants/defaultValues";
import cx from "classnames";
import { getErrors, isValid } from "@totalsoft/pure-validations-react";
import { HelpOutline } from "@material-ui/icons";
import { onCheckBoxChange, onTextBoxChange } from "utils/propertyChangeAdapters";
import { useTranslation } from "react-i18next";
import { questionVariableTypeCode } from "constants/questionVariableTypeCode";
import { normalizeDecimalToInvariant } from "utils/functions";

const useStyles = makeStyles(styles);

function QuestionComponent({ questionAndAnswer, validation, disabled }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const isDecimalAnswer =
    (questionAndAnswer.variableTypeCode |> get) === questionVariableTypeCode.percent ||
    (questionAndAnswer.variableTypeCode |> get) === questionVariableTypeCode.amount ||
    (questionAndAnswer.variableTypeCode |> get) === questionVariableTypeCode.numeric ||
    (questionAndAnswer.variableTypeCode |> get) === questionVariableTypeCode.percent6;
  const percentPrecision =
    (questionAndAnswer.variableTypeCode |> get) === questionVariableTypeCode.percent6 ? 6 : isDecimalAnswer ? 2 : 0;

  const handleChangeAutocomplete = useCallback(
    (value) => {
      (questionAndAnswer.questionAnswer |> set)(value.optionValueName);
    },
    [questionAndAnswer]
  );

  const handleChangeCustomTextField = useCallback(
    (value) => {
      (questionAndAnswer.questionAnswer |> set)(value || value === 0 ? value.toString() : null);
    },
    [questionAndAnswer]
  );

  const handleShowAdditionalInfo = useCallback(() => {
    setShowAdditionalInfo(!showAdditionalInfo);
  }, [setShowAdditionalInfo, showAdditionalInfo]);

  const answerType = useCallback(() => {
    if ((questionAndAnswer.variableTypeCode |> get) === questionVariableTypeCode.date) {
      return (
        <DateTime
          disableToolbar={false}
          disabled={disabled}
          value={questionAndAnswer.questionAnswer |> get}
          onChange={questionAndAnswer.questionAnswer |> set}
          error={!isValid(validation?.questionAnswer)}
          helperText={getErrors(validation?.questionAnswer)}
        />
      );
    } else if ((questionAndAnswer.optionIsRange |> get) && (questionAndAnswer.options |> get).length > 0) {
      return (
        <CustomTextField
          disabled={disabled}
          fullWidth
          value={
            (isDecimalAnswer
              ? normalizeDecimalToInvariant(questionAndAnswer.questionAnswer |> get)
              : questionAndAnswer.questionAnswer |> get) || emptyString
          }
          onChange={handleChangeCustomTextField}
          error={!isValid(validation?.questionAnswer)}
          helperText={getErrors(validation?.questionAnswer)}
          isNumeric={true}
          inputProps={{
            decimalScale: percentPrecision,
            thousandSeparator: true,
          }}
        />
      );
    } else if (!(questionAndAnswer.optionIsRange |> get) && (questionAndAnswer.options |> get).length > 0) {
      return (
        <Autocomplete
          disabled={disabled}
          valueKey="optionValueId"
          labelKey="optionValueName"
          options={questionAndAnswer.options |> get}
          value={{
            optionValueId:
              (questionAndAnswer?.options |> get)?.find(
                (x) => x.optionValueName === (questionAndAnswer.questionAnswer |> get)
              )?.optionValueId || emptyString,
            optionValueName: (questionAndAnswer.options |> get).find(
              (x) => x.optionValueName === (questionAndAnswer.questionAnswer |> get)
            )?.optionValueName,
          }}
          onChange={handleChangeAutocomplete}
          fullWidth
          error={!isValid(validation?.questionAnswer)}
          helperText={getErrors(validation?.questionAnswer)}
          noOptionsText={t("General.AutocompleteNoOptions")}
        />
      );
    } else if ((questionAndAnswer.variableTypeCode |> get) === questionVariableTypeCode.boolean) {
      return (
        <Checkbox
          disabled={disabled}
          checked={questionAndAnswer.questionAnswer |> get}
          onChange={questionAndAnswer.questionAnswer |> set |> onCheckBoxChange}
        />
      );
    } else {
      return (
        <CustomTextField
          fullWidth
          disabled={disabled}
          value={(questionAndAnswer.questionAnswer |> get) || emptyString}
          onChange={questionAndAnswer.questionAnswer |> set |> onTextBoxChange}
          error={!isValid(validation?.questionAnswer)}
          helperText={getErrors(validation?.questionAnswer)}
        />
      );
    }
  }, [
    questionAndAnswer,
    disabled,
    validation,
    percentPrecision,
    handleChangeAutocomplete,
    t,
    handleChangeCustomTextField,
    isDecimalAnswer,
  ]);

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={true} lg={8}>
        <Grid container>
          <Typography variant="h6" className={classes.reqText}>
            {questionAndAnswer.questionText |> get}
          </Typography>
          {(questionAndAnswer.isMandatory |> get) && (
            <Tooltip title={t("DataCompletion.QuestionsAndAnswers.AnswerRequired")}>
              <Typography variant="h6" className={cx(classes.reqText, classes.reqTextRed)}>
                {"*"}
              </Typography>
            </Tooltip>
          )}
        </Grid>
        {showAdditionalInfo && (
          <Typography className={classes.additionalInfo}>{questionAndAnswer.questionDescription |> get}</Typography>
        )}
      </Grid>
      <Grid item>
        <Tooltip title={questionAndAnswer.questionDescription |> get || emptyString}>
          <IconButton size="small" onClick={handleShowAdditionalInfo}>
            <HelpOutline fontSize="small" color="secondary" />
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid item xs={true}>
        {answerType()}
      </Grid>
    </Grid>
  );
}

QuestionComponent.propTypes = {
  questionAndAnswer: PropTypes.object.isRequired,
  disabled: PropTypes.bool.isRequired,
  validation: PropTypes.object.isRequired,
};

export default QuestionComponent;
